import { Link } from '@remix-run/react';
import { ArrowUpRight, Heart, Sparkles, SlidersHorizontal } from 'lucide-react';
import { openLoyaltySignIn, useLoyaltyShopping } from './loyalty-shopping';

const MENU_CARDS = [
  {
    eyebrow: 'Picked for you',
    title: 'Your recs',
    accent: 'here?',
    description: 'Less scrolling. More your thing. Sign in for a menu personalized to you.',
    cta: 'Make it mine',
    icon: Sparkles,
    theme: 'bg-[#264B9A] text-white',
    highlight: 'text-[#ffd100]',
    sticker: 'bg-[#ffd100] text-[#14294F]',
  },
  {
    eyebrow: 'A little more you',
    title: 'My favorite',
    accent: 'vape?',
    description: 'You know what you like. Make room for it in a menu that feels like yours.',
    cta: 'Find my favorites',
    icon: Heart,
    theme: 'bg-[#ffd100] text-[#14294F]',
    highlight: 'text-[#264B9A]',
    sticker: 'bg-[#264B9A] text-white',
  },
  {
    eyebrow: 'Your own kind of good',
    title: 'Your menu.',
    accent: 'Your way.',
    description: 'A menu as individual as your taste. Create an account to make it personal.',
    cta: 'Personalize my menu',
    icon: SlidersHorizontal,
    theme: 'bg-[#f3f0e7] text-[#14294F]',
    highlight: 'text-[#264B9A]',
    sticker: 'bg-[#264B9A] text-[#ffd100]',
  },
] as const;

// First appear at visual slots 6, 20 and 30, counting the inserted cards.
// Continue every 20 products so infinite scrolling gets occasional prompts too.
const INITIAL_PRODUCT_INDEXES = [5, 18, 27];

export function RewardsClubInsertion({ productIndex }: { productIndex: number }) {
  const { guest } = useLoyaltyShopping();
  const initialIndex = INITIAL_PRODUCT_INDEXES.indexOf(productIndex);
  const laterIndex = productIndex > 27 && (productIndex - 27) % 20 === 0
    ? 2 + (productIndex - 27) / 20
    : -1;
  const cardIndex = initialIndex >= 0 ? initialIndex : laterIndex;
  return guest && cardIndex >= 0 ? <RewardsClubCard variant={cardIndex % MENU_CARDS.length} /> : null;
}

export function RewardsClubCard({ variant = 0 }: { variant?: number }) {
  const card = MENU_CARDS[variant % MENU_CARDS.length];
  const Icon = card.icon;

  return (
    <aside
      aria-label={`${card.title} ${card.accent}`}
      className={`relative flex h-full min-h-80 w-full flex-col overflow-hidden rounded-2xl ${card.theme}`}
    >
      <div className="relative flex flex-1 flex-col items-start px-4 pt-5 pb-6 md:px-6 md:pt-7">
        <span aria-hidden="true" className="absolute -top-12 -right-12 h-44 w-44 rounded-full border-[28px] border-current opacity-[0.06]" />
        <div aria-hidden="true" className={`relative mb-7 flex h-16 w-16 -rotate-6 items-center justify-center rounded-2xl md:h-20 md:w-20 ${card.sticker}`}>
          <Icon className="h-8 w-8 md:h-10 md:w-10" strokeWidth={1.75} />
        </div>
        <p className="relative mb-3 text-[10px] font-bold tracking-[0.14em] uppercase md:text-xs">{card.eyebrow}</p>
        <h3 className="font-futura relative text-[28px] leading-[1.02] font-bold tracking-tight md:text-4xl">
          {card.title}<br />
          <span className={card.highlight}>{card.accent}</span>
        </h3>
        <p className="relative mt-4 max-w-64 text-xs leading-relaxed opacity-85 md:text-sm">{card.description}</p>
      </div>
      <div className="mx-4 space-y-1 border-t border-current/20 pt-2 pb-3 md:mx-6 md:pb-5">
        <Link to="/register" className="flex min-h-11 items-center justify-between gap-2 rounded-md py-2 text-sm font-bold focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-current">
          {card.cta}<ArrowUpRight aria-hidden="true" className="h-5 w-5 shrink-0" />
        </Link>
        <button type="button" onClick={openLoyaltySignIn} className="min-h-11 rounded-md text-left text-xs underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-current">
          Already have an account? Sign in
        </button>
      </div>
    </aside>
  );
}
